import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import {Button, Modal, ModalBody} from "reactstrap";
import {confirmAlert} from "react-confirm-alert";
import {Link} from "react-router-dom";
import moment from "moment-timezone";
import APPCONFIG from "constants/Config";
import {freshdeskTicketUrl} from "utils/freshdeskTicket";

const RESULT_LABELS = {
	pass: "Pass",
	fail: "Fail",
	na: "N/A",
};

const PHOENIX = "America/Phoenix";

const formatPhoenixDateTime = (value) => {
	if (value == null || value === "") {
		return "";
	}
	const raw = String(value).trim();
	if (!raw) {
		return "";
	}
	// A zone suffix is a real instant. A bare clock is already Phoenix wall time.
	const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
	const stamp = hasZone ? moment(raw).tz(PHOENIX) : moment.tz(raw, PHOENIX);
	return stamp.isValid() ? stamp.format("ddd MMM D, h:mm A z") : "";
};

/** Note append lines from the API use plain m/d/y h:i a; label Phoenix (MST) on display. */
const displayAuditWriteup = (writeup) => {
	if (!writeup) {
		return "";
	}
	return writeup
		.split("\n")
		.map((line) => {
			const trimmed = line.trimEnd();
			if (/^-.+ at \d{1,2}\/\d{1,2}\/\d{2,4} \d{1,2}:\d{2} [ap]m$/i.test(trimmed) && !/\b(MST|MDT|Phoenix)\b/i.test(trimmed)) {
				return trimmed + " MST";
			}
			return line;
		})
		.join("\n");
};

class TicketAudit extends Framework.Components.View {
	constructor(props) {
		super(props);
		this.state = {
			...(this.state || {}),
			loading: true,
			saving: false,
			loadError: "",
			saveError: "",
			feed: null,
			results: {},
			naConfirmed: {},
			writeup: "",
			newNote: "",
			noteSaving: false,
			noteError: "",
			feNote: "",
			feNoteOriginal: "",
			feNoteExists: false,
			feNoteSaving: false,
			feNoteError: "",
			attachmentSources: {},
			preview: "",
		};
		moment.tz.setDefault("America/Phoenix");
	}

	componentDidMount() {
		this.load();
	}

	componentWillUnmount() {
		this.loadSeq = 0;
	}

	componentDidUpdate(prevProps) {
		const previous = prevProps.match && prevProps.match.params ? prevProps.match.params.id : prevProps.id;
		const current = this.ticketId();
		if (previous !== current) {
			this.load();
		}
	}

	ticketId() {
		if (this.props.match && this.props.match.params && this.props.match.params.id) {
			return this.props.match.params.id;
		}
		return this.props.id;
	}

	load = async () => {
		const ticketId = this.ticketId();
		const seq = (this.loadSeq || 0) + 1;
		this.loadSeq = seq;
		this.setState({
			loading: true,
			loadError: "",
			saveError: "",
			noteError: "",
			newNote: "",
			feed: null,
			results: {},
			naConfirmed: {},
			writeup: "",
			feNote: "",
			feNoteOriginal: "",
			feNoteExists: false,
			feNoteError: "",
			attachmentSources: {},
		});
		try {
			const model = new API.BuildClass(Types.Ticket_Audits._name, API.RequestType.READ_ALL);
			model.filter = {ticket: ticketId};
			const feed = await model.submit(null, "GET_FEED");
			if (this.loadSeq !== seq || String(this.ticketId()) !== String(ticketId)) {
				return;
			}
			let results = (feed && feed.audit && feed.audit.results) || {};
			if (!(feed && feed.audit)) {
				results = {...results};
				((feed && feed.sections) || []).forEach((section) => {
					if (section.checklist !== false && section.waived && !results[section.key]) {
						results[section.key] = "na";
					}
				});
			}
			const helpdeskNote = (feed && feed.helpdesk_note) || null;
			this.setState({
				loading: false,
				feed,
				results,
				writeup: (feed && feed.audit && feed.audit.writeup) || "",
				feNote: (helpdeskNote && helpdeskNote.notes) || "",
				feNoteOriginal: (helpdeskNote && helpdeskNote.notes) || "",
				feNoteExists: !!helpdeskNote,
			}, () => this.loadMissingAttachments(feed, ticketId, seq));
		} catch (e) {
			if (this.loadSeq !== seq) {
				return;
			}
			this.setState({
				loading: false,
				loadError: (e && e.message) || "Unable to load this ticket audit.",
			});
		}
	}

	fileSrc(image) {
		const direct = image && image.url ? String(image.url) : "";
		if (/^https?:\/\//i.test(direct)) {
			return direct;
		}
		const fallback = image && this.state.attachmentSources[String(image.id)];
		return (fallback && fallback.src) || "";
	}

	loadMissingAttachments = async (feed, ticketId, seq) => {
		const missing = {};
		((feed && feed.notes) || []).forEach((note) => {
			(note.images || []).forEach((image) => {
				if (image && image.id && !/^https?:\/\//i.test(String(image.url || ""))) {
					missing[String(image.id)] = true;
				}
			});
		});

		await Promise.all(Object.keys(missing).map(async (attachmentId) => {
			try {
				const model = new API.BuildClass(Types.Ticket_Audits._name, API.RequestType.READ_ALL);
				model.filter = {ticket: ticketId, attachment: Number(attachmentId)};
				const result = await model.submit(null, "GET_ATTACHMENT");
				if (
					result
					&& result.success === true
					&& result.data_url
					&& this.loadSeq === seq
					&& String(this.ticketId()) === String(ticketId)
				) {
					this.setState((state) => ({
						attachmentSources: {
							...state.attachmentSources,
							[attachmentId]: {
								src: result.data_url,
								name: result.name || "",
								content_type: result.content_type || "",
							},
						},
					}));
				}
			} catch (e) {
				console.warn("Freshdesk attachment could not be loaded:", e);
			}
		}));
	}

	waiverUrl(pkey) {
		return APPCONFIG.settings.apiURL() + "/" + APPCONFIG.settings.apiVersion + "/forms/results/print/" + pkey;
	}

	fileIsImage(file) {
		const fallback = file && this.state.attachmentSources[String(file.id)];
		const type = String((file && file.content_type) || (fallback && fallback.content_type) || "").toLowerCase();
		const name = String((file && file.name) || (fallback && fallback.name) || "");
		if (type.indexOf("image/") === 0 || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name)) {
			return true;
		}
		if (type.indexOf("application/pdf") === 0 || /\.pdf$/i.test(name) || (type !== "" && name !== "")) {
			return false;
		}
		return true;
	}

	fileName(file) {
		const fallback = file && this.state.attachmentSources[String(file.id)];
		return String((file && file.name) || (fallback && fallback.name) || "File");
	}

	setResult = (key, value) => {
		this.setState({results: {...this.state.results, [key]: value}});
	}

	chooseNa = (section) => {
		const key = section.key;
		if (section.waived || !section.evidence) {
			this.setResult(key, "na");
			return;
		}
		confirmAlert({
			title: "Mark this step N/A?",
			message: "This step has notes or images and no recorded waiver. N/A is normally only for waived steps. Mark it N/A anyway?",
			buttons: [
				{
					label: "Mark N/A",
					onClick: () => {
						this.setState((s) => ({naConfirmed: {...s.naConfirmed, [key]: true}}));
						this.setResult(key, "na");
					},
				},
				{label: "Cancel", onClick: () => {}},
			],
		});
	}

	saveFeNote = async () => {
		const note = (this.state.feNote || "").trim();
		const original = (this.state.feNoteOriginal || "").trim();
		if (note === original) {
			return true;
		}
		if (note === "") {
			this.setState({feNoteError: this.state.feNoteExists ? "An existing Field Engineer note cannot be cleared." : ""});
			return !this.state.feNoteExists;
		}
		if (note.length > 3000) {
			this.setState({feNoteError: "Field Engineer note must be 3,000 characters or fewer."});
			return false;
		}

		const calendarPkey = this.state.feed && this.state.feed.calendar && this.state.feed.calendar.pkey;
		if (!calendarPkey) {
			this.setState({feNoteError: "This ticket has no calendar appointment for a Field Engineer note."});
			return false;
		}

		this.setState({feNoteSaving: true, feNoteError: ""});
		try {
			const model = new API.BuildClass(Types.Calendar_Help_Desk_Notes._name, API.RequestType.CREATE);
			model.parent = calendarPkey;
			model.object.calendars = calendarPkey;
			model.object.notes = note;

			const result = await model.submit(null, "PUT");
			if (!result || result.success !== true) {
				throw new Error((result && (result.message || result.error)) || "Unable to save the Field Engineer note.");
			}
			this.setState({
				feNote: note,
				feNoteOriginal: note,
				feNoteExists: true,
				feNoteSaving: false,
				feNoteError: "",
			});
			return true;
		} catch (e) {
			this.setState({
				feNoteSaving: false,
				feNoteError: (e && (e.message || e.error)) || "Unable to save the Field Engineer note.",
			});
			return false;
		}
	}

	blockPaste = (event) => {
		event.preventDefault();
	}

	canSubmit() {
		const feed = this.state.feed;
		if (!feed || feed.audit || feed.blocked_reason || this.state.saving || this.state.feNoteSaving) {
			return false;
		}
		if ((this.state.feNote || "").trim().length > 3000) {
			return false;
		}
		const writeup = (this.state.writeup || "").trim();
		if (writeup.length < 40) {
			return false;
		}
		return this.checklistSections(feed).every((section) => {
			const value = this.state.results[section.key];
			if (value !== "pass" && value !== "fail" && value !== "na") {
				return false;
			}
			if (value === "na" && section.evidence && !section.waived && !this.state.naConfirmed[section.key]) {
				return false;
			}
			return true;
		});
	}

	save = async () => {
		if (!this.canSubmit()) {
			return;
		}
		this.setState({saving: true, saveError: ""});
		try {
			if (!await this.saveFeNote()) {
				this.setState({saving: false});
				return;
			}
			const model = new API.BuildClass(Types.Ticket_Audits._name, API.RequestType.CREATE);
			model.object.ticket = Number(this.ticketId());
			model.object.writeup = this.state.writeup.trim();
			this.checklistSections(this.state.feed).forEach((section) => {
				model.object[section.key + "_result"] = this.state.results[section.key];
			});
			const overrideSections = this.checklistSections(this.state.feed).filter((section) =>
				this.state.results[section.key] === "na" && section.evidence && !section.waived
			);
			if (overrideSections.length > 0 && overrideSections.every((section) => this.state.naConfirmed[section.key])) {
				model.object.na_confirmed = true;
			}
			const result = await model.submit(null, "PUT");
			if (!result || result.success !== true) {
				const message = (result && (result.message || result.error)) || (typeof result === "string" ? result : "Unable to save this audit.");
				this.setState({saving: false, saveError: message});
				return;
			}
			this.setState({saving: false});
			await this.load();
		} catch (e) {
			this.setState({
				saving: false,
				saveError: (e && (e.message || e.error)) || "Unable to save this audit.",
			});
		}
	}

	addNote = async () => {
		const note = (this.state.newNote || "").trim();
		if (!note || this.state.noteSaving || this.noteInFlight) {
			return;
		}
		if (note.length > 20000) {
			this.setState({noteError: "Note must be 20,000 characters or fewer."});
			return;
		}
		const ticketId = this.ticketId();
		this.noteInFlight = true;
		this.setState({noteSaving: true, noteError: ""});
		try {
			const model = new API.BuildClass(Types.Ticket_Audits._name, API.RequestType.CREATE);
			model.object.ticket = Number(ticketId);
			model.object.note = note;
			const result = await model.submit(null, "POST_ADD_NOTE");
			if (!result || result.success !== true) {
				const message = (result && (result.message || result.error)) || (typeof result === "string" ? result : "Unable to save this note.");
				this.setState({noteSaving: false, noteError: message});
				return;
			}
			if (String(this.ticketId()) !== String(ticketId)) {
				this.setState({
					noteSaving: false,
					noteError: "Note saved on ticket " + ticketId + ".",
				});
				return;
			}
			this.setState({noteSaving: false, newNote: ""});
			await this.load();
		} catch (e) {
			let noteError = "Unable to save this note.";
			if (e && typeof e.message === "string" && e.message && e.message !== "[object Object]") {
				noteError = e.message;
			} else if (e && typeof e === "string") {
				noteError = e;
			}
			this.setState({
				noteSaving: false,
				noteError,
			});
		} finally {
			this.noteInFlight = false;
		}
	}

	checklistSections(feed) {
		return ((feed && feed.sections) || []).filter((section) => section.checklist !== false);
	}

	notesFor(sectionKey) {
		return ((this.state.feed && this.state.feed.notes) || []).filter((note) => note.section === sectionKey);
	}

	renderFinancial(financial) {
		if (!financial) {
			return null;
		}
		return (
			<div>
				{financial.payment_type ? <div>Payment: {financial.payment_type}</div> : null}
				{financial.labor_amount !== "" ? <div>Labor: {financial.labor_amount}</div> : null}
				{financial.virus_sales !== "" ? <div>Virus sales: {financial.virus_sales}</div> : null}
				{financial.parts_amount !== "" ? <div>Parts: {financial.parts_amount}</div> : null}
				{financial.total ? <div>Total: {financial.total}</div> : null}
				{financial.tap_paid ? <div>Paid tap: {financial.tap_amount}</div> : null}
				{financial.invoice_email_sent ? <div>Invoice email sent</div> : null}
				{financial.invoice_paid ? <div>Invoice paid</div> : null}
			</div>
		);
	}

	renderAttempts(attempts) {
		if (!attempts || attempts.length < 1) {
			return null;
		}
		return attempts.map((attempt) => (
			<div key={attempt.pkey} style={{marginTop: 8}}>
				<strong>{attempt.process} {attempt.stage} #{attempt.attempt_no}</strong>
				{" "}{attempt.outcome}
				{attempt.notes ? <div style={{whiteSpace: "pre-wrap"}}>{attempt.notes}</div> : null}
			</div>
		));
	}

	renderFeNoteEditor() {
		const note = this.state.feNote || "";
		const original = this.state.feNoteOriginal || "";
		const hasCalendar = !!(this.state.feed && this.state.feed.calendar && this.state.feed.calendar.pkey);
		const canSave = hasCalendar
			&& !this.state.feNoteSaving
			&& note.trim() !== ""
			&& note.trim() !== original.trim()
			&& note.trim().length <= 3000;

		return (
			<div style={{marginBottom: 16}}>
				<div style={{fontWeight: 700}}>Note for Field Engineer <span style={{fontWeight: 400}}>(optional)</span></div>
				<small style={{display: "block", marginBottom: 6}}>
					This note is shown to the Field Engineer in the mobile app. New notes must be opened and acknowledged by the Field Engineer. If a note is still unacknowledged after 3 days, the Field Engineer cannot start their first ticket of the day. Editing a note they already acknowledged does not require another acknowledgement.
				</small>
				<textarea
					value={note}
					onChange={(event) => this.setState({feNote: event.target.value, feNoteError: ""})}
					maxLength={3000}
					rows={4}
					disabled={!hasCalendar || this.state.feNoteSaving}
					style={{width: "100%"}}
				/>
				<div>{note.trim().length} / 3000</div>
				{!hasCalendar ? <small>This ticket has no calendar appointment for a Field Engineer note.</small> : null}
				{this.state.feNoteError ? <p>{this.state.feNoteError}</p> : null}
				<Button size="sm" color="info" disabled={!canSave} onClick={this.saveFeNote}>
					{this.state.feNoteSaving ? "Saving FE note" : "Save FE note"}
				</Button>
			</div>
		);
	}

	renderChecklist() {
		const feed = this.state.feed;
		const readOnly = !!(feed.audit || feed.blocked_reason);
		const results = feed.audit ? feed.audit.results : this.state.results;
		return (
			<aside className="ticket-audit-checklist">
				<h3 style={{marginTop: 0}}>Checklist</h3>
				{feed.audit ? (
					<p>Reviewed by {feed.audit.reviewer || "Admin"} {feed.audit.created ? "on " + formatPhoenixDateTime(feed.audit.created) : ""}</p>
				) : null}
				{feed.legacy_review && !feed.audit ? (
					<p>Checked by {feed.legacy_review.reviewer || "Admin"} {feed.legacy_review.created ? "on " + formatPhoenixDateTime(feed.legacy_review.created) : ""} before ticket audits. Complete this audit to record the new review.</p>
				) : null}
				{feed.blocked_reason ? <p>{feed.blocked_reason}</p> : null}
				{feed.freshdesk_error ? <p>{feed.freshdesk_error}</p> : null}
				{this.checklistSections(feed).map((section) => (
					<div key={section.key} style={{marginBottom: 16}}>
						<div style={{fontWeight: 700}}>{section.label}</div>
						{readOnly ? (
							<div>{RESULT_LABELS[results[section.key]] || ""}</div>
						) : (
							<div>
								{["pass", "fail", "na"].map((value) => (
									<Button
										key={value}
										size="sm"
										style={{marginRight: 4, marginTop: 4}}
										color={results[section.key] !== value ? "secondary" : value === "fail" ? "danger" : "primary"}
										onClick={() => (value === "na" ? this.chooseNa(section) : this.setResult(section.key, value))}
									>
										{RESULT_LABELS[value]}
									</Button>
								))}
							</div>
						)}
					</div>
				))}
				{this.renderFeNoteEditor()}
				<div style={{fontWeight: 700}}>Internal Notes</div>
				<small style={{display: "block", color: "#6c757d", marginBottom: 6}}>
					Internal Notes not visible to the Field Engineer
				</small>
				{readOnly ? (
					<div>
						<div style={{whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word"}}>{feed.audit ? displayAuditWriteup(feed.audit.writeup) : ""}</div>
						{feed.audit && !feed.blocked_reason ? (
							<div style={{marginTop: 12}}>
								<div style={{fontWeight: 700}}>Add a note</div>
								<textarea
									value={this.state.newNote}
									onChange={(event) => this.setState({newNote: event.target.value})}
									maxLength={20000}
									rows={4}
									style={{width: "100%"}}
								/>
								<div>Added notes are saved with your name and the time in Phoenix (MST).</div>
								{this.state.noteError ? <p>{this.state.noteError}</p> : null}
								<Button color="primary" disabled={this.state.noteSaving || !(this.state.newNote || "").trim()} onClick={this.addNote}>
									{this.state.noteSaving ? "Saving" : "Add note"}
								</Button>
							</div>
						) : null}
					</div>
				) : (
					<div>
						<textarea
							value={this.state.writeup}
							onChange={(event) => this.setState({writeup: event.target.value})}
							onPaste={this.blockPaste}
							onDrop={this.blockPaste}
							onKeyDown={(event) => {
								if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "v") {
									event.preventDefault();
								}
								if (event.shiftKey && event.key === "Insert") {
									event.preventDefault();
								}
							}}
							onBeforeInput={(event) => {
								if (event.nativeEvent && event.nativeEvent.inputType === "insertFromPaste") {
									event.preventDefault();
								}
							}}
							rows={8}
							style={{width: "100%"}}
						/>
						<div style={{color: (this.state.writeup || "").trim().length < 40 ? "#dc3545" : "inherit"}}>
							{(this.state.writeup || "").trim().length} / 40 minimum
						</div>
						<div>Type the internal notes. Paste is blocked.</div>
						{this.state.saveError ? <p>{this.state.saveError}</p> : null}
						<Button color="primary" disabled={!this.canSubmit()} onClick={this.save}>
							{this.state.saving ? "Saving" : "Mark reviewed"}
						</Button>
					</div>
				)}
			</aside>
		);
	}

	render() {
		const feed = this.state.feed;
		const ticket = feed && feed.ticket;
		const freshdeskUrl = ticket ? freshdeskTicketUrl(ticket.fresh_ticket) : null;
		const breadcrumbs = [
			{name: "Help Desks", url: "/helpdesks", icon: "comments"},
			{name: "Ticket Audit", url: "/helpdesks/audit", icon: "check-square"},
			{name: ticket ? String(ticket.pkey) : "Ticket", url: "/helpdesks/audit/" + this.ticketId(), icon: "file"},
		];

		return (
			<div className="container-fluid">
				<style>{`
					.ticket-audit-layout { display: flex; gap: 16px; align-items: flex-start; }
					.ticket-audit-main { flex: 1 1 auto; min-width: 0; }
					.ticket-audit-main section { scroll-margin-top: 80px; }
					.ticket-audit-checklist { width: 320px; flex: 0 0 320px; position: sticky; top: 12px; max-height: calc(100vh - 24px); overflow-x: hidden; overflow-y: auto; border: 1px solid #ddd; padding: 12px; background: #fff; }
					.ticket-audit-chip { margin: 0 6px 6px 0; }
					.ticket-audit-chip.na,
					.ticket-audit-chip.na:hover,
					.ticket-audit-chip.na:focus {
						background-color: #d9d9d9;
						border-color: #cfcfcf;
						color: #333;
					}
					@media (max-width: 1100px) {
						.ticket-audit-layout { flex-direction: column; }
						.ticket-audit-checklist { width: 100%; flex: none; position: static; max-height: none; }
					}
				`}</style>
				<Framework.Components.PageBar breadcrumbs={breadcrumbs} history={this.props.history}/>
				<Framework.Elements.LoadingSpinner isLoading={this.state.loading}/>
				{this.state.loadError ? <p>{this.state.loadError}</p> : null}
				{ticket ? (
					<div>
						<div style={{display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12}}>
							<div>
								<h2 style={{marginBottom: 4}}>Ticket Audit / {ticket.pkey}</h2>
								<div>
									{ticket.customer}
									{ticket.field_engineer ? " · " + ticket.field_engineer : ""}
									{ticket.appointment_start ? " · " + formatPhoenixDateTime(ticket.appointment_start) : ""}
									{ticket.same_day_cancel ? " · Same Day Cancel" : ""}
								</div>
							</div>
							<div>
								<Link to="/helpdesks/audit">Back to list</Link>
								{freshdeskUrl ? (
									<div><a href={freshdeskUrl} target="_blank" rel="noopener noreferrer">Open in Freshdesk</a></div>
								) : null}
							</div>
						</div>
						<div style={{marginBottom: 12}}>
							{(feed.sections || []).map((section) => {
								const value = (feed.audit && feed.audit.results[section.key]) || this.state.results[section.key];
								const chipColor = value === "pass" ? "primary" : value === "fail" ? "danger" : "secondary";
								return (
									<Button
										key={section.key}
										size="sm"
										className={"ticket-audit-chip" + (value === "na" ? " na" : "")}
										color={chipColor}
										onClick={() => {
											const node = document.getElementById("audit-" + section.key);
											if (node) {
												node.scrollIntoView({behavior: "smooth", block: "start"});
											}
										}}
									>
										{section.label}
									</Button>
								);
							})}
						</div>
						<div className="ticket-audit-layout">
							<div className="ticket-audit-main">
								{(feed.sections || []).map((section) => (
									<section id={"audit-" + section.key} key={section.key} style={{marginBottom: 28, paddingTop: 18, borderTop: "2px solid #ccc"}}>
										<h3>{section.label}</h3>
										{section.signed ? <div>Waiver or completion recorded</div> : null}
										{section.local_text ? <div style={{whiteSpace: "pre-wrap", marginBottom: 8}}>{section.local_text}</div> : null}
										{this.renderAttempts(section.attempts)}
										{this.renderFinancial(section.financial)}
										{(section.waivers || []).map((waiver) => (
											<div key={waiver.pkey}>
												<a href={this.waiverUrl(waiver.pkey)} target="_blank" rel="noopener noreferrer">{waiver.name || "Waiver"}</a>
											</div>
										))}
										{this.notesFor(section.key).map((note) => (
											<div key={note.id} style={{marginTop: 12, paddingTop: 8, borderTop: "1px solid #eee"}}>
												<div style={{color: "#666"}}>{note.created_at ? formatPhoenixDateTime(note.created_at) : ""}</div>
												<div style={{whiteSpace: "pre-wrap"}}>{note.text}</div>
												<div style={{display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8}}>
													{(note.images || []).map((image) => {
														const src = this.fileSrc(image);
														const name = this.fileName(image);
														const key = image.id || image.name || src;
														if (!src) {
															return <div key={key}>{name}</div>;
														}
														if (!this.fileIsImage(image)) {
															return (
																<div key={key}>
																	<a href={src} target="_blank" rel="noopener noreferrer">{name}</a>
																</div>
															);
														}
														return (
															<img
																key={key}
																src={src}
																alt={name}
																style={{width: 160, height: 120, objectFit: "cover", cursor: "pointer"}}
																onClick={() => this.setState({preview: src})}
															/>
														);
													})}
												</div>
											</div>
										))}
									</section>
								))}
							</div>
							{this.renderChecklist()}
						</div>
					</div>
				) : null}
				<Modal isOpen={!!this.state.preview} toggle={() => this.setState({preview: ""})} size="lg">
					<ModalBody>
						{this.state.preview ? <img src={this.state.preview} alt="" style={{width: "100%"}}/> : null}
					</ModalBody>
				</Modal>
			</div>
		);
	}
}

export default Framework.Components.withRootState(TicketAudit);
